import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Calculator, RefreshCw, DollarSign, TrendingDown, Sparkles, Info,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Label,
} from 'recharts'
import { api } from '../lib/api'

interface Model {
  id: string
  name: string
  provider: string
  price_input: number
  price_output: number
  speed_tps: number
  context_window: number
  open_weights: boolean
  composite: number
  benchmarks: Record<string, number>
}

const PROVIDER_COLOR: Record<string, string> = {
  Anthropic: '#d97757',
  OpenAI: '#10a37f',
  Google: '#4285f4',
  Meta: '#1877f2',
  Mistral: '#ff7000',
  DeepSeek: '#6466f1',
  xAI: '#1da1f2',
  Alibaba: '#ff6a00',
}

// Preset workloads — pick one and adjust
const PRESETS = [
  { id: 'chatbot', label: 'Customer chatbot', in_m: 50, out_m: 15, cached: 40, desc: '50M in / 15M out, heavy caching' },
  { id: 'agent', label: 'Coding agent', in_m: 200, out_m: 30, cached: 20, desc: '200M in / 30M out, moderate cache' },
  { id: 'summarise', label: 'Document summarisation', in_m: 500, out_m: 20, cached: 10, desc: '500M in / 20M out, low cache' },
  { id: 'copilot', label: 'Inline code copilot', in_m: 100, out_m: 50, cached: 60, desc: '100M in / 50M out, high cache' },
  { id: 'pilot', label: 'Small pilot', in_m: 5, out_m: 2, cached: 10, desc: '5M in / 2M out, testing' },
  { id: 'enterprise', label: 'Enterprise scale', in_m: 2000, out_m: 400, cached: 50, desc: '2B in / 400M out, heavy cache' },
]

function fmtUSD(n: number): string {
  if (n >= 10_000) return `$${(n / 1000).toFixed(1)}k`
  if (n >= 1_000) return `$${(n / 1000).toFixed(2)}k`
  if (n >= 1) return `$${n.toFixed(2)}`
  return `$${n.toFixed(4)}`
}

function fmtMonthly(n: number): string {
  return fmtUSD(n)
}

const tooltipStyle = {
  backgroundColor: '#18181c',
  border: '1px solid #26262c',
  borderRadius: 8,
  fontSize: 11,
  padding: '8px 10px',
}

export default function AICostCalculatorPage() {
  const [models, setModels] = useState<Model[]>([])
  const [loading, setLoading] = useState(true)

  const [inM, setInM] = useState(100)
  const [outM, setOutM] = useState(20)
  const [cached, setCached] = useState(30)
  const [preset, setPreset] = useState<string>('agent')

  const fetchModels = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/ai-models', { params: { sort: 'composite' } })
      setModels(data.models)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchModels() }, [fetchModels])

  // Apply preset when changed
  useEffect(() => {
    const p = PRESETS.find(p => p.id === preset)
    if (p) {
      setInM(p.in_m)
      setOutM(p.out_m)
      setCached(p.cached)
    }
  }, [preset])

  // Calculate costs per model
  const rows = useMemo(() => {
    // Assume cached tokens cost 10% of list price (Anthropic/OpenAI standard)
    const cachedFrac = Math.min(Math.max(cached / 100, 0), 1)
    return models.map(m => {
      const inFullCost = inM * (1 - cachedFrac) * m.price_input
      const inCachedCost = inM * cachedFrac * m.price_input * 0.1
      const outCost = outM * m.price_output
      const monthly = inFullCost + inCachedCost + outCost
      const perMillionIn = (1 - cachedFrac) * m.price_input + cachedFrac * m.price_input * 0.1
      const costPerScore = monthly > 0 && m.composite > 0 ? monthly / m.composite : 0
      return { ...m, monthly, perMillionIn, costPerScore, inFullCost, inCachedCost, outCost }
    }).sort((a, b) => a.monthly - b.monthly)
  }, [models, inM, outM, cached])

  const cheapest = rows[0]
  const mostExpensive = rows[rows.length - 1]
  const savings = cheapest && mostExpensive && mostExpensive.monthly > 0
    ? ((mostExpensive.monthly - cheapest.monthly) / mostExpensive.monthly) * 100
    : 0

  // Frontier chart data — cost vs composite
  const chartData = rows.map(r => ({
    x: r.monthly,
    y: r.composite,
    name: r.name,
    provider: r.provider,
    id: r.id,
  }))

  const maxCost = Math.max(...rows.map(r => r.monthly), 1)

  if (loading && models.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Calculator size={20} className="text-emerald" /> LLM Cost Calculator
          </h1>
          <p className="text-[10px] text-text-secondary">
            Plug in your workload. See monthly cost across {models.length} models + cost-per-benchmark-point.
          </p>
        </div>
      </div>

      {/* Inputs */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">
            Preset
          </span>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map(p => (
              <button
                type="button"
                key={p.id}
                onClick={() => setPreset(p.id)}
                className={clsx(
                  'px-2 py-1 text-[10px] rounded border transition-colors',
                  preset === p.id
                    ? 'bg-emerald/10 border-emerald/30 text-emerald'
                    : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
                )}
                title={p.desc}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
              Input tokens / month
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                value={inM}
                onChange={e => { setInM(Number(e.target.value)); setPreset('') }}
                className="input py-1.5 text-[12px] w-32 text-right font-mono"
                title="Input tokens per month in millions"
              />
              <span className="text-[10px] text-text-muted">million</span>
            </div>
            <input
              type="range"
              min={0}
              max={2000}
              step={1}
              value={Math.min(inM, 2000)}
              onChange={e => { setInM(Number(e.target.value)); setPreset('') }}
              className="w-full mt-2 accent-emerald"
              title="Input volume slider"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
              Output tokens / month
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                value={outM}
                onChange={e => { setOutM(Number(e.target.value)); setPreset('') }}
                className="input py-1.5 text-[12px] w-32 text-right font-mono"
                title="Output tokens per month in millions"
              />
              <span className="text-[10px] text-text-muted">million</span>
            </div>
            <input
              type="range"
              min={0}
              max={500}
              step={1}
              value={Math.min(outM, 500)}
              onChange={e => { setOutM(Number(e.target.value)); setPreset('') }}
              className="w-full mt-2 accent-emerald"
              title="Output volume slider"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
              Cached input (%)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={100}
                value={cached}
                onChange={e => { setCached(Number(e.target.value)); setPreset('') }}
                className="input py-1.5 text-[12px] w-32 text-right font-mono"
                title="Percentage of input tokens hitting prompt cache"
              />
              <span className="text-[10px] text-text-muted">%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={cached}
              onChange={e => { setCached(Number(e.target.value)); setPreset('') }}
              className="w-full mt-2 accent-emerald"
              title="Cache hit rate slider"
            />
          </div>
        </div>

        <div className="flex items-start gap-1.5 text-[10px] text-text-muted pt-1">
          <Info size={11} className="shrink-0 mt-0.5" />
          <span>
            Cached input billed at 10% of list price (standard across Anthropic & OpenAI). Cost assumes list
            prices — enterprise discounts not included. Token counts are approximate; actual usage depends on
            your tokenizer.
          </span>
        </div>
      </div>

      {/* Summary */}
      {cheapest && mostExpensive && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="card p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingDown size={12} className="text-emerald" />
              <span className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">
                Cheapest
              </span>
            </div>
            <div className="text-sm font-bold text-text-primary">{cheapest.name}</div>
            <div className="text-xs font-mono text-emerald mt-1">{fmtMonthly(cheapest.monthly)}/mo</div>
            <div className="text-[10px] text-text-muted">composite {cheapest.composite.toFixed(1)}</div>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <DollarSign size={12} className="text-rose" />
              <span className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">
                Most expensive
              </span>
            </div>
            <div className="text-sm font-bold text-text-primary">{mostExpensive.name}</div>
            <div className="text-xs font-mono text-rose mt-1">{fmtMonthly(mostExpensive.monthly)}/mo</div>
            <div className="text-[10px] text-text-muted">composite {mostExpensive.composite.toFixed(1)}</div>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Sparkles size={12} className="text-amber" />
              <span className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">
                Max savings
              </span>
            </div>
            <div className="text-sm font-bold text-text-primary">
              {fmtMonthly(mostExpensive.monthly - cheapest.monthly)}
            </div>
            <div className="text-xs font-mono text-amber mt-1">{savings.toFixed(0)}% cheaper</div>
            <div className="text-[10px] text-text-muted">cheapest vs most expensive</div>
          </div>
        </div>
      )}

      {/* Frontier chart */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-text-primary">Cost vs Quality Frontier</h2>
          <span className="text-[10px] text-text-muted">bottom-right = best value</span>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 20, bottom: 24, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#26262c" />
              <XAxis
                type="number"
                dataKey="x"
                name="Monthly cost"
                tick={{ fill: '#71717a', fontSize: 10 }}
                tickFormatter={(v: number) => fmtUSD(v)}
                domain={[0, maxCost * 1.05]}
              >
                <Label value="Monthly cost (USD)" offset={-12} position="insideBottom" fill="#71717a" fontSize={10} />
              </XAxis>
              <YAxis
                type="number"
                dataKey="y"
                name="Composite"
                tick={{ fill: '#71717a', fontSize: 10 }}
                domain={[60, 90]}
              >
                <Label value="Composite score" angle={-90} position="insideLeft" fill="#71717a" fontSize={10} />
              </YAxis>
              <ZAxis range={[80, 80]} />
              <Tooltip
                contentStyle={tooltipStyle}
                cursor={{ strokeDasharray: '3 3' }}
                formatter={(value: number, name: string) => {
                  if (name === 'Monthly cost') return [fmtUSD(value), name]
                  if (name === 'Composite') return [value.toFixed(1), name]
                  return [value, name]
                }}
                labelFormatter={() => ''}
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null
                  const p = payload[0].payload
                  return (
                    <div style={tooltipStyle}>
                      <div style={{ fontWeight: 600, color: '#fff' }}>{p.name}</div>
                      <div style={{ color: PROVIDER_COLOR[p.provider] || '#999', fontSize: 10 }}>
                        {p.provider}
                      </div>
                      <div style={{ color: '#a1a1aa', fontSize: 10, marginTop: 4 }}>
                        {fmtUSD(p.x)}/mo · composite {p.y.toFixed(1)}
                      </div>
                    </div>
                  )
                }}
              />
              <Scatter data={chartData}>
                {chartData.map((d, i) => (
                  <Cell key={i} fill={PROVIDER_COLOR[d.provider] || '#4b5563'} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Cost table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-surface-raised/50">
              <tr className="border-b border-border">
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 w-8">#</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Model</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Provider</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 text-right">Input $/M eff</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 text-right">Output $/M</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 text-right">Composite</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 text-right">Monthly</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3 text-right">$/score-pt</th>
                <th className="text-[10px] text-text-muted font-semibold py-2 px-3">vs cheapest</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const multiple = cheapest && cheapest.monthly > 0 ? r.monthly / cheapest.monthly : 1
                const barWidth = maxCost > 0 ? (r.monthly / maxCost) * 100 : 0
                return (
                  <tr key={r.id} className={clsx(
                    'border-b border-border/50 hover:bg-surface-raised/50 transition-colors',
                    i === 0 && 'bg-emerald/5'
                  )}>
                    <td className="py-2 px-3 text-[11px] font-mono text-text-muted">{i + 1}</td>
                    <td className="py-2 px-3">
                      <div className="text-[11px] font-semibold text-text-primary">{r.name}</div>
                      <div className="text-[9px] text-text-muted">
                        ctx {r.context_window >= 1_000_000 ? `${r.context_window / 1_000_000}M` : `${r.context_window / 1000}K`}
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <span
                        className="text-[10px] font-semibold"
                        style={{ color: PROVIDER_COLOR[r.provider] || '#999' }}
                      >
                        {r.provider}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right text-[10px] font-mono text-text-secondary">
                      ${r.perMillionIn.toFixed(2)}
                    </td>
                    <td className="py-2 px-3 text-right text-[10px] font-mono text-text-secondary">
                      ${r.price_output.toFixed(2)}
                    </td>
                    <td className="py-2 px-3 text-right text-[10px] font-mono text-text-secondary">
                      {r.composite.toFixed(1)}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 bg-surface-raised rounded-full overflow-hidden">
                          <div
                            className={clsx('h-full', i === 0 ? 'bg-emerald' : 'bg-blue')}
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                        <span className={clsx(
                          'text-[11px] font-mono font-bold w-16 text-right',
                          i === 0 ? 'text-emerald' : 'text-text-primary'
                        )}>
                          {fmtMonthly(r.monthly)}
                        </span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right text-[10px] font-mono text-text-secondary">
                      {fmtUSD(r.costPerScore)}
                    </td>
                    <td className="py-2 px-3 text-[10px] text-text-muted">
                      {i === 0 ? (
                        <span className="text-emerald font-semibold">baseline</span>
                      ) : (
                        <>{multiple.toFixed(1)}× more</>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[10px] text-text-muted">
        <Info size={10} className="inline mr-1" />
        "Input $/M eff" = effective price per million input tokens after cache discount. "$/score-pt" =
        cost efficiency: monthly cost divided by composite benchmark score (lower = better value).
      </div>
    </div>
  )
}
